import React from 'react';
import { useTheme } from '../hooks/useTheme';

interface PricingPageProps {
  t: (key: string) => any;
}

const PricingPage: React.FC<PricingPageProps> = ({ t }) => {
  const { theme } = useTheme();

  return (
    <div className="min-h-screen">
      {/* Pricing Hero */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
            {t('pricing.title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-10">
            {t('pricing.description')}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Public APIs */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('pricing.publicAPIs.title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('pricing.publicAPIs.description')}
                </p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">{t('pricing.publicAPIs.model')}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {Array.isArray(t('pricing.publicAPIs.features')) ? t('pricing.publicAPIs.features').map((feature: string, idx: number) => (
                    <li key={idx} className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                    </li>
                  )) : null}
                  <li className="text-gray-600 dark:text-gray-400 italic mt-4">
                    {t('pricing.publicAPIs.note')}
                  </li>
                </ul>
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors">
                  {t('pricing.publicAPIs.cta')}
                </button>
              </div>
            </div>

            {/* Tenant-Specific APIs */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border-2 border-blue-500 relative transform scale-105">
              <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-sm font-medium rounded-bl-lg">
                {t('pricing.popular')}
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('pricing.tenantAPIs.title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('pricing.tenantAPIs.description')}
                </p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">{t('pricing.tenantAPIs.business.price')}</span>
                  <span className="text-gray-600 dark:text-gray-400">{t('pricing.tenantAPIs.business.period')}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {Array.isArray(t('pricing.tenantAPIs.business.features')) ? t('pricing.tenantAPIs.business.features').map((feature: string, idx: number) => (
                    <li key={idx} className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                    </li>
                  )) : null}
                  <li className="text-gray-600 dark:text-gray-400 italic mt-4">
                    {t('pricing.tenantAPIs.note')}
                  </li>
                </ul>
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors">
                  {t('pricing.tenantAPIs.cta')}
                </button>
              </div>
            </div>

            {/* Enterprise */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('pricing.tenantAPIs.enterprise.title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('pricing.tenantAPIs.description')}
                </p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">{t('pricing.tenantAPIs.enterprise.price')}</span>
                  <span className="text-gray-600 dark:text-gray-400">{t('pricing.tenantAPIs.enterprise.period')}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {Array.isArray(t('pricing.tenantAPIs.enterprise.features')) ? t('pricing.tenantAPIs.enterprise.features').map((feature: string, idx: number) => (
                    <li key={idx} className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                    </li>
                  )) : null}
                  <li className="text-gray-600 dark:text-gray-400 italic mt-4">
                    {t('pricing.tenantAPIs.note')}
                  </li>
                </ul>
                <button className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors">
                  {t('pricing.tenantAPIs.enterprise.cta')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('pricing.faq.title')}
          </h2>
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('pricing.faq.q1.question')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('pricing.faq.q1.answer')}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('pricing.faq.q2.question')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('pricing.faq.q2.answer')}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('pricing.faq.q3.question')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('pricing.faq.q3.answer')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-800 dark:to-indigo-900">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('pricing.cta.title')}
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-10">
            {t('pricing.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-medium text-lg transition-colors">
              {t('pricing.cta.primary')}
            </button>
            <button className="bg-transparent border-2 border-white text-white hover:bg-blue-700 px-8 py-4 rounded-lg font-medium text-lg transition-colors">
              {t('pricing.cta.secondary')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
